import { Link } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { StatCard } from '@/components/StatCard';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { OnboardingNotice } from '@/components/OnboardingNotice';
import { StatusBadge, statusLabel } from '@/components/StatusBadge';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatCurrencyMinor, formatDate, formatDateTime, formatNumber } from '@/i18n/format';

/**
 * COMMERCIAL LAYER epic (ADR-075 Part C). The seller's landing screen -
 * one aggregate read (`GET /api/v1/portal/dashboard`, apps/api/src/
 * portalReads.ts's `portalDashboard`), not seven separate requests: stock
 * summary, open orders, orders needing attention, recent returns, recent
 * charges, unpaid/overdue invoices, recent evidence - exactly the epic's
 * own spec for this screen.
 */
export function DashboardPage(): JSX.Element {
  const result = useApi(() => endpoints.dashboard(), []);
  const { t, locale } = useLanguage();

  if (result.status === 'loading') return <LoadingState label={t('dashboard.loading')} />;
  if (result.status === 'error') return <ErrorBanner error={result.error} onRetry={result.reload} />;

  const d = result.data;

  return (
    <div>
      <div className="page-header">
        <h1>{t('nav.dashboard')}</h1>
      </div>
      <OnboardingNotice />

      <div className="grid-cards">
        <StatCard label={t('dashboard.skusInStock')} value={formatNumber(d.stock.sku_count, locale)} />
        <StatCard label={t('dashboard.unitsAvailable')} value={formatNumber(d.stock.qty_available, locale)} />
        <StatCard label={t('dashboard.unitsOnHand')} value={formatNumber(d.stock.total_on_hand, locale)} />
        <StatCard label={t('dashboard.unitsReserved')} value={formatNumber(d.stock.total_reserved, locale)} />
        <StatCard label={t('dashboard.unitsQuarantine')} value={formatNumber(d.stock.qty_quarantine, locale)} />
        <StatCard label={t('dashboard.unitsInProcess')} value={formatNumber(d.stock.qty_in_process, locale)} />
        <StatCard label={t('dashboard.unitsShipped')} value={formatNumber(d.stock.qty_shipped, locale)} />
        <StatCard label={t('dashboard.openOrders')} value={formatNumber(d.openOrders, locale)} href="/shipments" />
        <StatCard label={t('dashboard.ordersNeedingAttention')} value={formatNumber(d.ordersNeedingAttention, locale)} href="/shipments" />
        <StatCard label={t('dashboard.unpaidInvoices')} value={formatNumber(d.unpaidInvoices.length, locale)} href="/billing" />
      </div>

      <div className="card">
        <h2 style={{ fontSize: 15, marginTop: 0 }}>{t('dashboard.unpaidOverdueInvoices')}</h2>
        {d.unpaidInvoices.length === 0 ? (
          <p className="sub">{t('dashboard.nothingOutstanding')}</p>
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('dashboard.colInvoice')}</th>
                <th>{t('common.status')}</th>
                <th>{t('dashboard.colDue')}</th>
                <th style={{ textAlign: 'right' }}>{t('dashboard.colBalance')}</th>
              </tr>
            </thead>
            <tbody>
              {d.unpaidInvoices.map((inv) => (
                <tr key={inv.id}>
                  <td>
                    <Link to="/billing">{inv.number ?? t('dashboard.draft')}</Link>
                  </td>
                  <td>
                    <StatusBadge value={inv.displayStatus ?? inv.state} />
                  </td>
                  <td>{inv.due_at ? formatDate(inv.due_at, locale) : '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    {formatCurrencyMinor(inv.balance_minor ?? String(Number(inv.total_minor) - Number(inv.paid_minor)), inv.currency, locale)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      <div className="card">
        <h2 style={{ fontSize: 15, marginTop: 0 }}>{t('dashboard.recentCharges')}</h2>
        {d.recentCharges.length === 0 ? (
          <p className="sub">{t('dashboard.noChargesYet')}</p>
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('dashboard.colKind')}</th>
                <th>{t('dashboard.colOccurred')}</th>
                <th style={{ textAlign: 'right' }}>{t('dashboard.colAmount')}</th>
              </tr>
            </thead>
            <tbody>
              {d.recentCharges.map((c) => (
                <tr key={c.id}>
                  <td>{statusLabel(t, c.kind)}</td>
                  <td>{formatDateTime(c.occurred_at, locale)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrencyMinor(c.amount_minor, c.currency, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      <div className="card">
        <h2 style={{ fontSize: 15, marginTop: 0 }}>{t('dashboard.recentReturns')}</h2>
        {d.recentReturns.length === 0 ? (
          <p className="sub">{t('dashboard.noRecentReturns')}</p>
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('dashboard.colReturn')}</th>
                <th>{t('dashboard.discrepancy')}</th>
                <th>{t('dashboard.colCreated')}</th>
              </tr>
            </thead>
            <tbody>
              {d.recentReturns.map((r) => (
                <tr key={r.id}>
                  <td>
                    <Link to="/returns">{r.number}</Link>
                  </td>
                  <td>
                    <StatusBadge value={r.discrepancy} />
                  </td>
                  <td>{formatDateTime(r.created_at, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      <div className="card">
        <h2 style={{ fontSize: 15, marginTop: 0 }}>{t('dashboard.recentEvidence')}</h2>
        {d.recentEvidence.length === 0 ? (
          <p className="sub">{t('dashboard.noEvidenceYet')}</p>
        ) : (
          <ul style={{ paddingLeft: 18 }}>
            {d.recentEvidence.map((e) => (
              <li key={e.id} className="sub">
                {t('dashboard.evidenceLine', {
                  kind: statusLabel(t, e.kind),
                  entity: statusLabel(t, e.entity_kind),
                  date: formatDateTime(e.created_at, locale),
                })}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
